/**
 * AppLauncher Component
 *
 * Microsoft 365-style waffle menu for navigating between apps.
 * Features a 9-dot grid button that opens a dropdown with app tiles.
 *
 * Features:
 * - 9-dot grid button (waffle icon)
 * - Dropdown with categorized app tiles organized by dashboard sections
 * - Uses adminEditorStore for app grid items
 * - Filters to only visible items, sorted by order
 * - Click outside closes the launcher
 * - Keyboard accessible (Escape to close)
 * - Dashboard card sections with visual headers and icons
 *
 * @example
 * ```tsx
 * // In TopBar
 * <AppLauncher />
 * ```
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Building2,
  FileText,
  Phone,
  Mail,
  Radio,
  Terminal,
  Database,
  RefreshCw,
  Receipt,
  Kanban,
  Clock,
  Brain,
  BookOpen,
  CalendarDays,
  Upload,
  Inbox,
  SendHorizonal,
  FolderOpen,
  LayoutGrid,
  Search,
  Target,
  Bot,
  LucideIcon,
} from 'lucide-react';
import { ROUTES } from '@/app/routes';
import { useVisibleAppGridItems } from '@features/admin/store/adminEditorStore';
import { AppLauncherTile } from './AppLauncherTile';

// ============================================================================
// Dashboard Card Configuration
// ============================================================================

/**
 * Dashboard card item for the launcher menu.
 * Matches the structure from DashboardView.tsx card configurations.
 */
interface DashboardCardItem {
  id: string;
  icon: LucideIcon;
  iconColor: string;
  title: string;
  description: string;
  route: string;
}

/**
 * Dashboard section with cards.
 */
interface DashboardSection {
  id: string;
  title: string;
  cards: DashboardCardItem[];
}

/**
 * Get all dashboard sections with their cards.
 * Organized to match the 5 sections from DashboardView.
 *
 * NOTE: This is a function (not a constant) to avoid circular dependency
 * with ROUTES. ROUTES is defined in routes.tsx which imports AppShell,
 * which imports AppLauncher. If we access ROUTES at module init time,
 * it causes "Cannot access 'ROUTES' before initialization" error.
 */
const getDashboardSections = (): DashboardSection[] => [
  {
    id: 'campaign-quick-access',
    title: 'Campaign Quick-Access',
    cards: [
      {
        id: 'event-planning',
        icon: Calendar,
        iconColor: 'purple',
        title: 'Event Planning',
        description: 'Plan and schedule events on the 12-month annual calendar grid',
        route: ROUTES.EPG,
      },
      {
        id: 'organizations',
        icon: Building2,
        iconColor: 'green',
        title: 'Organizations',
        description: 'Browse and manage organizations from your Pipedrive CRM',
        route: ROUTES.ORGANIZATIONS,
      },
    ],
  },
  {
    id: 'post-campaign-execution',
    title: 'Post-Campaign',
    cards: [
      {
        id: 'weekly-drop-report',
        icon: FileText,
        iconColor: 'green',
        title: 'Weekly Drop Report',
        description: 'View dropped organizations grouped by week',
        route: ROUTES.WEEKLY_DROP_REPORT,
      },
      {
        id: 'weekly-call-status',
        icon: Phone,
        iconColor: 'blue',
        title: 'Weekly Call Status',
        description: 'Track call activities by time period',
        route: ROUTES.WEEKLY_CALL_STATUS,
      },
      {
        id: 'follow-ups',
        icon: Mail,
        iconColor: 'orange',
        title: 'Follow-Ups',
        description: 'Manage email follow-ups for your drops',
        route: ROUTES.DROP_FOLLOW_UPS,
      },
    ],
  },
  {
    id: 'administrative',
    title: 'Administrative',
    cards: [
      {
        id: 'vibe-remote',
        icon: Radio,
        iconColor: 'brand',
        title: 'Vibe Remote',
        description: 'Remote terminal sessions',
        route: ROUTES.VIBE_REMOTE,
      },
      {
        id: 'ops-monitor',
        icon: Bot,
        iconColor: 'brand',
        title: 'Ops Monitor',
        description: 'Live commit feed & session activity',
        route: ROUTES.CLAWD_BOT,
      },
      {
        id: 'inbox',
        icon: Inbox,
        iconColor: 'blue',
        title: 'Inbox',
        description: 'Outlook inbox monitoring and message triage',
        route: ROUTES.INBOX,
      },
      {
        id: 'cli-dashboard',
        icon: Terminal,
        iconColor: 'gray',
        title: 'CLI Dashboard',
        description: 'Monitor CLI sessions in real-time',
        route: ROUTES.CLI_DASHBOARD,
      },
      {
        id: 'field-management',
        icon: Database,
        iconColor: 'cyan',
        title: 'Field Management',
        description: 'Manage Pipedrive field definitions',
        route: ROUTES.FIELD_MANAGEMENT,
      },
      {
        id: 'pug-records',
        icon: RefreshCw,
        iconColor: 'green',
        title: 'PUG Records',
        description: 'View Pipedrive sync records',
        route: ROUTES.PUG_RECORDS,
      },
      {
        id: 'expenses',
        icon: Receipt,
        iconColor: 'orange',
        title: 'Expenses',
        description: 'Upload receipts with OCR',
        route: ROUTES.EXPENSES,
      },
      {
        id: 'project-board',
        icon: Kanban,
        iconColor: 'purple',
        title: 'Project Board',
        description: 'Kanban-style project management',
        route: ROUTES.PLANKA,
      },
      {
        id: 'file-manager',
        icon: FolderOpen,
        iconColor: 'blue',
        title: 'File Manager',
        description: 'Manage files and screenshots',
        route: ROUTES.FILE_MANAGER,
      },
      {
        id: 'boards',
        icon: LayoutGrid,
        iconColor: 'cyan',
        title: 'Boards',
        description: 'Weekly planning and task boards',
        route: ROUTES.BOARDS,
      },
    ],
  },
  {
    id: 'client-management',
    title: 'Client Management',
    cards: [
      {
        id: 'client-timelines',
        icon: Calendar,
        iconColor: 'cyan',
        title: 'Client Timelines',
        description: 'Manage enrollment timelines',
        route: ROUTES.TIMELINES,
      },
      {
        id: 'availability',
        icon: Clock,
        iconColor: 'purple',
        title: 'Availability',
        description: 'Find meeting times across teams',
        route: ROUTES.AVAILABILITY,
      },
    ],
  },
  {
    id: 'sales-tools',
    title: 'Sales Tools',
    cards: [
      {
        id: 'flashcards',
        icon: Brain,
        iconColor: 'purple',
        title: 'Flashcards',
        description: 'Study outreach scripts and tips',
        route: ROUTES.FLASHCARDS,
      },
      {
        id: 'notes',
        icon: BookOpen,
        iconColor: 'blue',
        title: 'Notes',
        description: 'Organize notes and documents',
        route: ROUTES.NOTES,
      },
      {
        id: 'houston-calendar',
        icon: CalendarDays,
        iconColor: 'orange',
        title: 'Houston Calendar',
        description: 'Marketing calendar for Houston events',
        route: ROUTES.CALENDAR,
      },
      {
        id: 'hmc-events',
        icon: Upload,
        iconColor: 'cyan',
        title: 'HMC Events',
        description: 'Upload HMC files and view reports',
        route: ROUTES.HMC_EVENTS,
      },
      {
        id: 'file-inbox',
        icon: Inbox,
        iconColor: 'green',
        title: 'File Inbox',
        description: 'Email attachments from Gmail',
        route: ROUTES.FILE_INBOX,
      },
      {
        id: 'usi-events',
        icon: SendHorizonal,
        iconColor: 'purple',
        title: 'USI Events',
        description: 'Send postcard emails to contacts',
        route: ROUTES.USI_EVENTS,
      },
      {
        id: 'opportunities-analyzer',
        icon: Search,
        iconColor: 'orange',
        title: 'Opportunities Analyzer',
        description: 'Compare OMNI vs Pipedrive opportunities',
        route: ROUTES.OPPORTUNITIES_ANALYZER,
      },
      {
        id: 'business-plan',
        icon: Target,
        iconColor: 'green',
        title: 'Business Plan',
        description: 'Manage campaign business plans and targets',
        route: ROUTES.BUSINESS_PLAN,
      },
    ],
  },
];

/**
 * Map icon color names to Tailwind CSS classes
 */
const ICON_COLOR_CLASSES: Record<string, string> = {
  purple: 'text-purple-400',
  green: 'text-green-400',
  brand: 'text-brand-500',
  blue: 'text-blue-400',
  orange: 'text-orange-400',
  gray: 'text-dark-muted',
  cyan: 'text-cyan-400',
};

// ============================================================================
// Waffle Icon Component
// ============================================================================

/**
 * 3x3 dot grid icon (waffle/app launcher icon).
 * Custom SVG component matching Microsoft 365 style.
 */
const WaffleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Row 1 */}
    <circle cx="5" cy="5" r="2" />
    <circle cx="12" cy="5" r="2" />
    <circle cx="19" cy="5" r="2" />
    {/* Row 2 */}
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
    {/* Row 3 */}
    <circle cx="5" cy="19" r="2" />
    <circle cx="12" cy="19" r="2" />
    <circle cx="19" cy="19" r="2" />
  </svg>
);

// ============================================================================
// Component Props
// ============================================================================

export interface AppLauncherProps {
  /** Additional CSS classes for the container */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * App Launcher component with waffle menu pattern.
 * Displays visible app grid items in a dropdown grid.
 * Includes all dashboard card sections organized by category.
 */
export const AppLauncher: React.FC<AppLauncherProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Get visible app grid items sorted by order (legacy app grid)
  const appGridItems = useVisibleAppGridItems();

  // Dashboard sections for the launcher (memoized, calls function to avoid circular dep)
  const dashboardSections = useMemo(() => getDashboardSections(), []);

  // ========================================================================
  // Event Handlers
  // ========================================================================

  /**
   * Handle tile click - navigate and close launcher
   */
  const handleTileClick = useCallback(
    (route: string) => {
      navigate(route);
      setIsOpen(false);
    },
    [navigate]
  );

  /**
   * Toggle launcher open/closed
   */
  const toggleLauncher = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  /**
   * Close launcher
   */
  const closeLauncher = useCallback(() => {
    setIsOpen(false);
  }, []);

  // ========================================================================
  // Click Outside Handler
  // ========================================================================

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // ========================================================================
  // Render
  // ========================================================================

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Waffle Button */}
      <button
        type="button"
        onClick={toggleLauncher}
        className="p-2 rounded-lg hover:bg-dark-surface transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500/50"
        aria-label="Open app launcher"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <WaffleIcon className="w-5 h-5 text-dark-muted hover:text-dark-text-primary transition-all duration-150" />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute left-0 top-full mt-1 z-50 bg-dark-surface border border-dark-border rounded-xl shadow-2xl shadow-black/20 min-w-[320px] max-w-[400px] max-h-[70vh] overflow-y-auto"
          role="menu"
          aria-orientation="vertical"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-dark-border/30">
            <h2 className="text-sm font-medium text-dark-text-primary">Apps</h2>
          </div>

          {/* Dashboard Sections with Cards */}
          <div className="py-2">
            {dashboardSections.map((section, sectionIndex) => (
              <div key={section.id}>
                {/* Section Header */}
                <div className="px-4 py-2">
                  <span className="text-xs font-medium text-dark-muted uppercase tracking-wider">
                    {section.title}
                  </span>
                </div>

                {/* Section Cards */}
                <div className="px-2 pb-2">
                  {section.cards.map((card) => {
                    const Icon = card.icon;
                    const colorClass = ICON_COLOR_CLASSES[card.iconColor] || 'text-brand-500';

                    return (
                      <button
                        key={card.id}
                        type="button"
                        onClick={() => handleTileClick(card.route)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-dark-surface/50 transition-all duration-150 text-left group"
                        role="menuitem"
                        title={card.description}
                      >
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-dark-bg ${colorClass}`}>
                          <Icon size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-dark-text-primary truncate">
                            {card.title}
                          </div>
                          <div className="text-xs text-dark-muted truncate">
                            {card.description}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Section Divider (except for last section) */}
                {sectionIndex < dashboardSections.length - 1 && (
                  <div className="mx-4 border-b border-dark-border/30" />
                )}
              </div>
            ))}
          </div>

          {/* Legacy App Grid (if needed) */}
          {appGridItems.length > 0 && (
            <>
              <div className="mx-4 border-b border-dark-border/30" />
              <div className="px-4 py-2">
                <span className="text-xs font-medium text-dark-muted uppercase tracking-wider">
                  More Apps
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 pt-0">
                {appGridItems.map((item) => (
                  <AppLauncherTile
                    key={item.id}
                    item={item}
                    onClick={() => handleTileClick(item.route)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AppLauncher;
